// src/components/Arena.js
import React, { useState, useMemo } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { GameRulesButton } from './GameRules';

const FORMATIONS = {
    '4-4-2': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'CF' }, { id: 'FW2', role: 'FW', line: 'FW', label: 'ST' },
        { id: 'MF1', role: 'MF', line: 'MF', label: 'LSH' }, { id: 'MF2', role: 'MF', line: 'MF', label: 'CMF' }, { id: 'MF3', role: 'MF', line: 'MF', label: 'CMF' }, { id: 'MF4', role: 'MF', line: 'MF', label: 'RSH' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'LSB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF4', role: 'DF', line: 'DF', label: 'RSB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ],
    '4-3-3': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'LWG' }, { id: 'FW2', role: 'FW', line: 'FW', label: 'CF' }, { id: 'FW3', role: 'FW', line: 'FW', label: 'RWG' },
        { id: 'MF1', role: 'MF', line: 'AMF', label: 'CMF' }, { id: 'MF3', role: 'MF', line: 'AMF', label: 'CMF' },
        { id: 'MF2', role: 'MF', line: 'DMF', label: 'DMF' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'LSB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF4', role: 'DF', line: 'DF', label: 'RSB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ],
    '3-5-2': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'CF' }, { id: 'FW2', role: 'FW', line: 'FW', label: 'ST' },
        { id: 'MF1', role: 'MF', line: 'AMF', label: 'LSH' }, { id: 'MF2', role: 'MF', line: 'AMF', label: 'OMF' }, { id: 'MF5', role: 'MF', line: 'AMF', label: 'RSH' },
        { id: 'MF3', role: 'MF', line: 'DMF', label: 'DMF' }, { id: 'MF4', role: 'MF', line: 'DMF', label: 'DMF' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ],
    '4-2-3-1': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'CF' },
        { id: 'MF1', role: 'MF', line: 'AMF', label: 'LSH' }, { id: 'MF2', role: 'MF', line: 'AMF', label: 'OMF' }, { id: 'MF3', role: 'MF', line: 'AMF', label: 'RSH' },
        { id: 'MF4', role: 'MF', line: 'DMF', label: 'DMF' }, { id: 'MF5', role: 'MF', line: 'DMF', label: 'DMF' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'LSB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF4', role: 'DF', line: 'DF', label: 'RSB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ]
};

const ROLE_MAP = {
    'CF': 'FW', 'ST': 'FW', 'LWG': 'FW', 'RWG': 'FW', 'WG': 'FW', 'FW': 'FW',
    'OMF': 'MF', 'CMF': 'MF', 'DMF': 'MF', 'LSH': 'MF', 'RSH': 'MF', 'SMF': 'MF', 'MF': 'MF',
    'CB': 'DF', 'LSB': 'DF', 'RSB': 'DF', 'DF': 'DF',
    'GK': 'GK', 'FP': 'FP', '未設定': 'FP'
};

const Arena = ({ user, users = [], appConfig = {}, onBack, onStartMatch }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    
    const hasPlayedFreeToday = user?.lastArenaDate === todayStr;
    const arenaDaily = user?.arenaDaily?.date === todayStr ? user.arenaDaily : { date: todayStr, paidCount: 0, foughtIds: [] };
    const MAX_PAID_COUNT = 5; 
    const paidCountLeft = MAX_PAID_COUNT - (arenaDaily.paidCount || 0);

    const getAptitudeMultiplier = (cardPos, slotRole, slotLabel) => {
        if (cardPos === slotLabel) return 1.0; 
        const cardMainRole = ROLE_MAP[cardPos] || 'FP';
        if (cardMainRole === slotRole) return 0.8; 
        if (cardMainRole === 'FP' && slotRole !== 'GK') return 0.8; 
        if (cardMainRole === 'GK' && slotRole !== 'GK') return 0.1; 
        if (cardMainRole !== 'GK' && slotRole === 'GK') return 0.1; 
        return 0.5; 
    };

    const calculateTeamStats = (deck, formationKey) => {
        let stats = { offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0, total: 0 };
        if (!deck || !formationKey || !FORMATIONS[formationKey]) return stats;
        
        FORMATIONS[formationKey].forEach(slot => {
            const card = deck[slot.id];
            if (card && card.stats) {
                const mult = getAptitudeMultiplier(card.position, slot.role, slot.label);
                stats.offense += Math.floor(card.stats.offense * mult);
                stats.defense += Math.floor(card.stats.defense * mult);
                stats.stamina += Math.floor(card.stats.stamina * mult);
                stats.speed += Math.floor(card.stats.speed * mult);
                stats.technique += Math.floor(card.stats.technique * mult);
            }
        });
        stats.total = stats.offense + stats.defense + stats.stamina + stats.speed + stats.technique;
        return stats;
    };

    const myStats = useMemo(() => calculateTeamStats(user?.deck, user?.formation), [user]);
    const myPower = myStats.total;

    const rivals = useMemo(() => {
        return users
            .filter(u => u.userId !== user?.userId && u.deck && Object.keys(u.deck).length > 0)
            .map(u => {
                const rStats = calculateTeamStats(u.deck, u.formation);
                return { ...u, teamStats: rStats, teamPower: rStats.total };
            })
            .filter(u => u.teamPower > 0) 
            .sort((a, b) => b.teamPower - a.teamPower)
            .slice(0, 10); // ★ここを追加！POWER順で並び替えた後、上位10名だけで足切りします
    }, [users, user?.userId]);

    const filteredRivals = rivals.filter(r => r.displayName.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleChallenge = async (rival) => {
        if (!user?.deck || Object.keys(user.deck).length === 0) {
            return Swal.fire('エラー', '先に「スタメン編成」で自分のチームを作ってください！', 'warning');
        }

        let isPaid = false;

        if (hasPlayedFreeToday) {
            if (arenaDaily.paidCount >= MAX_PAID_COUNT) {
                return Swal.fire('上限到達', `本日の有償対戦の上限（${MAX_PAID_COUNT}回）に達しました。\nまた明日挑戦してください！`, 'error');
            }

            const currentPoints = user.points || 0;
            // ★参加費チェックを50に変更！
            if (currentPoints < 50) {
                return Swal.fire('コイン不足', '本日の無料分は終了しました。再挑戦には50RBコイン必要です。', 'error');
            }

            // ★ポップアップのテキストを50RBと新報酬に変更！
            const confirmPay = await Swal.fire({
                title: '無料分終了',
                html: `本日の無料分は終了しています。<br><b>50 RBコイン</b> を消費して挑戦しますか？<br><br><small>勝てば100RB、引分50RB、負ければ0RB</small>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: '50RB 支払って挑む！',
                confirmButtonColor: '#e65100',
                cancelButtonText: 'やめる'
            });

            if (!confirmPay.isConfirmed) return;
            isPaid = true;
        }

        Swal.fire({
            title: '試合を開始しますか？',
            html: `
                <div style="display:flex; justify-content:space-between; align-items:center; margin-top:15px; padding:10px; background:#f5f5f5; border-radius:8px;">
                    <div style="text-align:center; flex:1;">
                        <div style="font-size:12px; font-weight:bold; color:#1976d2;">YOU</div>
                        <div style="font-size:24px; font-weight:900; color:#333;">${myPower}</div>
                    </div>
                    <div style="font-size:20px; font-weight:bold; color:#d32f2f; margin:0 10px;">VS</div>
                    <div style="text-align:center; flex:1;">
                        <div style="font-size:12px; font-weight:bold; color:#e65100;">${rival.displayName}</div>
                        <div style="font-size:24px; font-weight:900; color:#333;">${rival.teamPower}</div>
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: '⚽️ キックオフ！',
            confirmButtonColor: '#d32f2f',
            cancelButtonText: 'やめる'
        }).then((result) => {
            if (result.isConfirmed) {
                onStartMatch(rival, myStats, isPaid);
            }
        });
    };

    return (
        <div style={{ minHeight: '100vh', background: '#111', fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>
            <Toaster position="top-center" />

            <div style={{ background: 'linear-gradient(135deg, #1a237e, #b71c1c)', padding: '15px 20px', display: 'flex', alignItems: 'center', color: 'white', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'white', fontSize: '16px', cursor: 'pointer', padding: '0 10px 0 0' }}>←</button>
                <h2 style={{ margin: 0, fontSize: '18px', flex: 1, textAlign: 'center', letterSpacing: '2px' }}>🔥 アリーナ 🏟️</h2>
                {/* ★ タイプ相性などのルールを公開する */}
                <GameRulesButton appConfig={appConfig} label="📖 ルール" />
            </div>

            <div style={{ background: '#222', padding: '15px', borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#555', border: '2px solid #1976d2', overflow: 'hidden' }}>
                        {user?.pictureUrl ? <img src={user.pictureUrl} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/> : <span style={{fontSize:'24px', textAlign:'center', display:'block'}}>👤</span>}
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#aaa' }}>マイチーム</div>
                        <div style={{ fontSize: '14px', color: 'white', fontWeight: 'bold' }}>{user?.displayName} 監督</div>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '10px', color: '#1976d2', fontWeight: 'bold' }}>TOTAL POWER</div>
                    <div style={{ fontSize: '24px', color: '#ffeb3b', fontWeight: '900', textShadow: '1px 1px 2px black' }}>{myPower || 0}</div>
                </div>
            </div>

            {/* ★画面の帯のテキストも50RBに変更！ */}
            <div style={{ background: hasPlayedFreeToday ? (paidCountLeft > 0 ? '#b71c1c' : '#424242') : '#2e7d32', color: 'white', textAlign: 'center', padding: '8px', fontSize: '12px', fontWeight: 'bold', boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.3)' }}>
                {hasPlayedFreeToday ? (
                    paidCountLeft > 0 
                    ? `⚠️ 無料分終了 / 有償挑戦: 残り ${paidCountLeft} 回 (1回50RB)`
                    : `🛑 本日の挑戦上限（${MAX_PAID_COUNT}回）に達しました`
                ) : (
                    '✨ 本日の無料挑戦：残り 1 回 ✨'
                )}
            </div>

            <div style={{ padding: '15px' }}>
                <input 
                    type="text" 
                    placeholder="🔍 ライバルを検索..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#333', color: 'white', marginBottom: '20px', boxSizing: 'border-box' }}
                />

                <div style={{ fontSize: '14px', color: '#ffd700', fontWeight: 'bold', marginBottom: '10px', borderBottom: '2px solid #555', paddingBottom: '5px' }}>
                    ⚔️ 対戦相手リスト
                </div>

                {filteredRivals.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888', marginTop: '40px', fontSize: '14px' }}>
                        対戦相手が見つかりません。<br/>スタメンを登録しているメンバーがここに表示されます。
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {filteredRivals.map((rival, idx) => {
                            const isFought = arenaDaily.foughtIds.includes(rival.userId);

                            return (
                                <div key={rival.userId} style={{ background: 'linear-gradient(90deg, #2a2a2a, #1a1a1a)', borderRadius: '12px', padding: '12px', display: 'flex', alignItems: 'center', borderLeft: `4px solid ${myPower >= rival.teamPower ? '#4caf50' : '#d32f2f'}`, boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
                                    
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#666', width: '25px', textAlign: 'center', marginRight: '10px' }}>
                                        {idx + 1}
                                    </div>

                                    <div style={{ width: '45px', height: '45px', borderRadius: '50%', background: '#555', border: '2px solid #fff', overflow: 'hidden', marginRight: '15px', flexShrink: 0 }}>
                                        {rival.pictureUrl ? <img src={rival.pictureUrl} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/> : <span style={{fontSize:'28px', textAlign:'center', display:'block', marginTop:'2px'}}>👤</span>}
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', color: 'white', fontWeight: 'bold', marginBottom: '4px' }}>{rival.displayName} 監督</div>
                                        <div style={{ fontSize: '11px', color: '#aaa', display: 'flex', gap: '10px' }}>
                                            <span>陣形: {rival.formation || '未設定'}</span>
                                            <span style={{ color: myPower >= rival.teamPower ? '#69f0ae' : '#ff5252' }}>
                                                {myPower >= rival.teamPower ? '有利' : '格上'}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ textAlign: 'right', marginRight: '15px' }}>
                                        <div style={{ fontSize: '10px', color: '#999' }}>POWER</div>
                                        <div style={{ fontSize: '18px', color: '#ffeb3b', fontWeight: '900' }}>{rival.teamPower}</div>
                                    </div>

                                    {isFought ? (
                                        <button 
                                            disabled
                                            style={{ background: '#555', color: '#999', border: '1px solid #777', borderRadius: '8px', padding: '10px', fontWeight: 'bold', cursor: 'not-allowed', fontSize: '12px' }}
                                        >
                                            ✅ 対戦済
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => handleChallenge(rival)}
                                            style={{ background: 'linear-gradient(135deg, #d32f2f, #b71c1c)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 15px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 5px rgba(211,47,47,0.4)' }}
                                        >
                                            挑む
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Arena;